import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowForwardRounded,
  CheckCircleRounded,
  DescriptionRounded,
  GroupsRounded,
  RefreshRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import { openGeneratedPdf } from '../../lib/open-generated-pdf';
import { doc, getDoc } from 'firebase/firestore';
import {
  MEMBERSHIP_MILESTONE_KEYS,
  MEMBERSHIP_STAGES,
  nextStage,
  stageRank,
  TMAI_MEMBERSHIP_FRAMEWORK_2022_03,
  type MembershipReadinessReport,
  type MembershipStage,
  type RosterAnnex,
} from '@dosfilos/domain';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { NetworkSubmissionSection } from './components/NetworkSubmissionSection';
import { usePermissions } from '../../hooks/use-permissions';
import {
  useMembershipMutations,
  useMembershipProfile,
  useMembershipReadiness,
} from '../../hooks/use-membership';
import { CapPageHeader, CAP_CONTENT_MAX_WIDTH } from './components/CapPageHeader';
import { errorText } from '../../lib/error-text';
import {
  MembershipPacketPdfDocument,
  type MembershipPacketCriterion,
  type MembershipPacketPdfData,
} from './MembershipPacketPdfDocument';
import {
  MembershipRosterAnnexPdfDocument,
  type MembershipRosterAnnexPdfData,
} from './MembershipRosterAnnexPdfDocument';
import { formatDate, inLocale } from '../../lib/i18n-format';

// English criterion labels for the TMAI packet, keyed by criterion id.
const CRITERION_LABELS_EN = new Map(
  TMAI_MEMBERSHIP_FRAMEWORK_2022_03.criteria.map((c) => [c.id, c.label.en]),
);

// ─── Institutional requirements board (Camino TMAI, R0) ──────────────────────
// The front door to the membership module: it names the target stage, shows the
// Afiliado→Miembro ladder, and — once readiness has run — the compliance picture
// derived from the TC's own data. The TMAI board decides membership; the platform
// only surfaces evidence and gaps.

// The ladder never shows 'notStarted' (it's the pre-entry state, not a rung).
const LADDER_STAGES = MEMBERSHIP_STAGES.filter((s) => s !== 'notStarted');

export function InstitutionalBoardPage(): JSX.Element {
  const { t, i18n } = useTranslation('membership');
  const navigate = useNavigate();
  const tenantId = useTenantStore((s) => s.currentTenantId);

  const { isAdmin, hasRole } = usePermissions();
  // The board (stage, readiness, packet) is staff-only — its data callables
  // require admin/registrar. A faculty member's Camino TMAI surface is the
  // doctrinal self-sign page.
  const isStaff = isAdmin || hasRole('registrar');
  const profileQ = useMembershipProfile();
  const readinessQ = useMembershipReadiness();
  const { setStage, runReadiness, assemblePacket, buildRosterAnnex } = useMembershipMutations();

  const [targetDraft, setTargetDraft] = useState<MembershipStage>('affiliate');
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [annexDialogOpen, setAnnexDialogOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // El puntero de conveniencia del tenant hacia su red. NO es la fuente de verdad
  // de la afiliación —eso lo declara la red— pero decide si tiene sentido ofrecer
  // la sección de presentar. Si falta, nadie afilió a esta institución todavía.
  //
  // ⚠️ ACÁ ARRIBA, CON LOS DEMÁS HOOKS. La primera versión los declaró después de
  // los `return` de carga y error, y React reventó con "Rendered more hooks than
  // during the previous render": el primer render sale por el early-return y no
  // llega a este `useState`; el siguiente sí, y el orden de hooks cambia. Ningún
  // test lo vio — typecheck, unit y build pasaron los tres — porque es una regla
  // del RUNTIME de React, no del tipo. Se detectó abriendo la página.
  const [networkTenantId, setNetworkTenantId] = useState<string | undefined>();
  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    void getDoc(doc(db, 'tenants', tenantId)).then((snap) => {
      if (alive)
        setNetworkTenantId(snap.data()?.['config']?.['networkTenantId'] as string | undefined);
    });
    return () => {
      alive = false;
    };
  }, [tenantId]);

  // Sync the drafts with the persisted profile so the change-stage dialog opens
  // on the current values.
  useEffect(() => {
    const p = profileQ.data;
    if (!p) return;
    // La etapa ALCANZADA ya no se edita — la declara TMAI. Solo se sincroniza el
    // objetivo, que sí es del seminario. Sin objetivo fijado, el selector arranca
    // en el escalón SIGUIENTE al alcanzado, no en un 'affiliate' fijo que para un
    // TC avanzado significaría retroceder.
    const next = nextStage(p.currentStage);
    setTargetDraft(p.targetStage ?? next ?? 'member');
  }, [profileQ.data]);

  const saveStage = async () => {
    if (!tenantId) return;
    setError(null);
    try {
      await setStage.mutateAsync({
        tenantId,
        // Sin `currentStage`: el callable lo RECHAZA de forma ruidosa. La etapa
        // alcanzada la confirma TMAI al aprobar una postulación.
        targetStage: targetDraft,
      });
      setToast(t('board.entry.saved'));
      setStageDialogOpen(false);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const recalc = async () => {
    if (!tenantId) return;
    setError(null);
    try {
      await runReadiness.mutateAsync({ tenantId });
    } catch (e) {
      setError(errorText(e));
    }
  };

  // Tenant name + TMAI site abbreviation for the PDF header (public-read doc;
  // best-effort). Header shows the NAME, not the id.
  const resolveBrand = async (): Promise<{ tenantName: string; tmaiSiteAbbreviation?: string }> => {
    let tenantName = tenantId ?? '';
    let tmaiSiteAbbreviation: string | undefined;
    if (tenantId) {
      try {
        const tData = (await getDoc(doc(db, `tenants/${tenantId}`))).data() ?? {};
        tenantName = (tData['name'] as string | undefined) ?? tenantName;
        const cfg = (tData['config'] ?? {}) as { tmaiSiteAbbreviation?: string };
        if (cfg.tmaiSiteAbbreviation) tmaiSiteAbbreviation = cfg.tmaiSiteAbbreviation;
      } catch {
        /* best-effort */
      }
    }
    return { tenantName, ...(tmaiSiteAbbreviation ? { tmaiSiteAbbreviation } : {}) };
  };

  // Build the aggregate, zero-PII packet from a freshness-gated snapshot. Labels
  // resolve to ENGLISH regardless of the UI language (TMAI submits in English).
  const buildPacketData = async (
    snapshot: MembershipReadinessReport,
  ): Promise<MembershipPacketPdfData> => {
    const en = i18n.getFixedT('en', 'membership');
    const origin = (
      derived: MembershipReadinessReport['criteria'][number]['derived'],
    ): string | undefined => {
      const d = derived?.detail;
      if (!d) return undefined;
      return d.denominator !== undefined
        ? en('packet.originCount', { numerator: d.numerator, denominator: d.denominator })
        : en('packet.originSingle', { numerator: d.numerator });
    };
    const view = (c: MembershipReadinessReport['criteria'][number]): MembershipPacketCriterion => ({
      label: CRITERION_LABELS_EN.get(c.criterionId) ?? c.criterionId,
      category: en(`categories.${c.category}`),
      ...(origin(c.derived) ? { origin: origin(c.derived)! } : {}),
    });

    const met: MembershipPacketCriterion[] = [];
    const gaps: MembershipPacketCriterion[] = [];
    const notEvaluable: MembershipPacketCriterion[] = [];
    const informative: MembershipPacketCriterion[] = [];
    for (const c of snapshot.criteria) {
      const v = view(c);
      if (c.status === 'met' || c.status === 'not_applicable') met.push(v);
      else if (c.status === 'no_data') notEvaluable.push(v);
      else if (c.gating)
        gaps.push(v); // not_met / partial on a gating criterion
      else informative.push(v); // non-gating unmet (declaration / informative)
    }

    return {
      brand: { ...(await resolveBrand()), primaryColor: '#334155' },
      stage: en(`stages.${snapshot.stage}`),
      generatedOn: inLocale('en-US').date(new Date()),
      overallStatus: snapshot.overallStatus,
      byCategory: snapshot.byCategory.map((c) => ({
        label: en(`categories.${c.category}`),
        met: c.met,
        total: c.total,
      })),
      met,
      gaps,
      notEvaluable,
      informative,
      labels: {
        title: en('packet.title'),
        watermarkDraft: en('packet.watermarkDraft'),
        stage: en('packet.stage'),
        generatedOn: en('packet.generatedOn'),
        overallStatusTitle: en('packet.overallStatusTitle'),
        overallReady: en('board.overallStatus.ready'),
        overallWithGaps: en('board.overallStatus.with_gaps'),
        progressByCategory: en('board.progressByCategory'),
        metTitle: en('packet.buckets.met'),
        gapsTitle: en('packet.buckets.gaps'),
        notEvaluableTitle: en('packet.buckets.notEvaluable'),
        notEvaluableReason: en('packet.buckets.notEvaluableReason'),
        informativeTitle: en('packet.buckets.informative'),
        boardDecides: en('packet.boardDecides'),
        footerDisclaimer: en('packet.footerDisclaimer'),
        emptyBucket: en('packet.emptyBucket'),
      },
    };
  };

  const generatePacket = async () => {
    if (!tenantId || !readinessQ.data) return;
    setError(null);
    const readinessReportId = readinessQ.data.id;
    try {
      // The tab opens inside the click; the packet is assembled into it.
      await openGeneratedPdf(async () => {
        // Freshness gate: the callable re-validates that this snapshot is still the
        // latest; if the analysis changed, it throws and we ask for a recalc.
        const snapshot = await assemblePacket.mutateAsync({ tenantId, readinessReportId });
        const data = await buildPacketData(snapshot);
        return pdf(<MembershipPacketPdfDocument data={data} />).toBlob();
      }, 'membership-packet');
    } catch (e) {
      setError(errorText(e));
    }
  };

  const buildAnnexData = async (annex: RosterAnnex): Promise<MembershipRosterAnnexPdfData> => {
    const en = i18n.getFixedT('en', 'membership');
    return {
      brand: { ...(await resolveBrand()), primaryColor: '#334155' },
      generatedOn: inLocale('en-US').date(new Date()),
      ministryDataGap: annex.ministryDataGap,
      rows: annex.rows.map((r) => ({
        fullName: r.fullName,
        kindLabel: en(`annex.kind.${r.kind}`),
        ...(r.location ? { location: r.location } : {}),
        ...(r.organizationName ? { organizationName: r.organizationName } : {}),
      })),
      labels: {
        title: en('annex.title'),
        piiWarning: en('annex.piiWarning'),
        generatedOn: en('packet.generatedOn'),
        colName: en('annex.col.name'),
        colType: en('annex.col.type'),
        colLocation: en('annex.col.location'),
        colOrg: en('annex.col.organization'),
        ministryGapNote: en('annex.ministryGapNote'),
        empty: en('annex.empty'),
        footerDisclaimer: en('annex.footerDisclaimer'),
      },
    };
  };

  const generateAnnex = async () => {
    if (!tenantId) return;
    setError(null);
    try {
      const annex = await buildRosterAnnex.mutateAsync({ tenantId });
      const data = await buildAnnexData(annex);
      const blob = await pdf(<MembershipRosterAnnexPdfDocument data={data} />).toBlob();
      window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
      setAnnexDialogOpen(false);
    } catch (e) {
      setError(errorText(e));
    }
  };

  // Escalones que el seminario puede elegir como objetivo: los que están por
  // encima del alcanzado. El alcanzado sale del espejo que declara la red.
  const achieved = profileQ.data?.currentStage ?? 'notStarted';
  const selectableTargets = LADDER_STAGES.filter((s) => stageRank(s) > stageRank(achieved));

  const stagePicker = (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {/* Etapa ALCANZADA: SOLO LECTURA. La declara TMAI al aprobar una
          postulación — un seminario no puede afirmar por su cuenta que es
          Miembro. Se muestra igual porque es la referencia desde la que se
          elige el objetivo. */}
      <TextField
        fullWidth
        disabled
        label={t('board.entry.currentStageLabel')}
        value={t(`stages.${profileQ.data?.currentStage ?? 'notStarted'}`)}
        helperText={
          profileQ.data?.stageConfirmedAt
            ? t('board.entry.stageConfirmed', {
                date: formatDate(profileQ.data.stageConfirmedAt),
              })
            : t('board.entry.stageNotConfirmed')
        }
      />
      <TextField
        select
        fullWidth
        label={t('board.entry.targetStageLabel')}
        value={targetDraft}
        onChange={(e) => setTargetDraft(e.target.value as MembershipStage)}
      >
        {/* Solo escalones POR ENCIMA del alcanzado. Antes ofrecía los cuatro y
            arrancaba fijo en 'affiliate', así que a un TC recién ascendido la
            pantalla le proponía RETROCEDER — y el rechazo llegaba recién al
            someter, tras meses de trabajo contra un objetivo imposible. */}
        {selectableTargets.map((s) => (
          <MenuItem key={s} value={s}>
            {t(`stages.${s}`)}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );

  // ── Loading / error ──────────────────────────────────────────────────────
  if (!isStaff) {
    return <Navigate to="/cap/institucional/atestaciones" replace />;
  }

  if (profileQ.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }
  if (profileQ.error || !profileQ.data) {
    return (
      <Box sx={{ px: 3, py: 3, maxWidth: CAP_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        <Alert severity="error">{t('board.loadError')}</Alert>
      </Box>
    );
  }

  const profile = profileQ.data;
  const readiness = readinessQ.data ?? null;

  // ── Pantalla de objetivo ─────────────────────────────────────────────────
  // El gate lee EXACTAMENTE el campo que esta pantalla escribe: `targetStage`.
  // Elegir objetivo lo llena, y eso te saca — es estructuralmente imposible
  // quedar atrapado.
  //
  // Antes keyeaba en `updatedAt` justamente para escapar de esa trampa: el gate
  // original miraba `currentStage`, un campo que la pantalla NO escribía, así que
  // guardar no sacaba a nadie de ahí. `updatedAt` lo esquivaba sin resolverlo.
  //
  // `targetStage === null` es un solo estado con dos caras: el TC que nunca
  // empezó, y el que ACABA DE SUBIR de escalón (al avanzar se limpia el
  // objetivo). Son el mismo estado — "no hay objetivo fijado" — y solo cambia el
  // texto, no el flujo.
  const needsObjective = profile.targetStage === null;
  const objectiveVariant: 'firstTime' | 'advanced' =
    profile.currentStage === 'notStarted' ? 'firstTime' : 'advanced';
  if (needsObjective) {
    return (
      <Box>
        <CapPageHeader
          backLabel={t(['board.back', 'actions.close'])}
          onBack={() => navigate(-1)}
          title={t('board.title')}
          subtitle={t('board.subtitle')}
        />
        <Box sx={{ px: 3, py: 3, pb: 6, maxWidth: 720, mx: 'auto' }}>
          <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, borderRadius: 4 }}>
            <Typography variant="h5" fontWeight={800} mb={1}>
              {t(`board.entry.${objectiveVariant}.title`)}
            </Typography>
            <Typography color="text.secondary" mb={3}>
              {t(`board.entry.${objectiveVariant}.body`)}
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {stagePicker}
            <Button
              variant="contained"
              sx={{ mt: 3 }}
              startIcon={setStage.isPending ? <CircularProgress size={16} /> : undefined}
              disabled={setStage.isPending}
              onClick={() => void saveStage()}
            >
              {t('board.entry.save')}
            </Button>
          </Paper>
        </Box>
        <Snackbar
          open={toast !== null}
          autoHideDuration={3000}
          onClose={() => setToast(null)}
          message={toast ?? ''}
        />
      </Box>
    );
  }

  // ── Board ────────────────────────────────────────────────────────────────
  return (
    <Box>
      <CapPageHeader
        backLabel={t(['board.back', 'actions.close'])}
        onBack={() => navigate(-1)}
        title={t('board.title')}
        subtitle={t('board.subtitle')}
        right={
          <Button
            startIcon={runReadiness.isPending ? <CircularProgress size={16} /> : <RefreshRounded />}
            onClick={() => void recalc()}
            disabled={runReadiness.isPending}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {t('board.recalc')}
          </Button>
        }
      />

      <Box sx={{ px: 3, py: 3, pb: 6, maxWidth: CAP_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity="info" icon={false} sx={{ mb: 3 }}>
          {t('board.boardDecidesNote')}
        </Alert>

        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
          {/* ── Left: ladder + milestones + stage controls ───────────────── */}
          <Box sx={{ width: { xs: '100%', lg: 380 }, flexShrink: 0 }}>
            <Stack spacing={2.5}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 1, fontWeight: 700 }}
                >
                  {t('board.ladder.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5} mb={2}>
                  {t('board.targetStageIs', { stage: t(`stages.${profile.targetStage}`) })}
                </Typography>
                <Stack spacing={1.5}>
                  {LADDER_STAGES.map((s) => {
                    const isCurrent = s === profile.currentStage;
                    const isTarget = s === profile.targetStage;
                    const accent = isCurrent
                      ? 'primary.main'
                      : isTarget
                        ? 'success.main'
                        : 'divider';
                    return (
                      <Paper
                        key={s}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 3,
                          borderLeft: '5px solid',
                          borderLeftColor: accent,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                          <Typography fontWeight={700} sx={{ flex: 1 }}>
                            {t(`stages.${s}`)}
                          </Typography>
                          {isCurrent && (
                            <Chip size="small" color="primary" label={t('board.ladder.current')} />
                          )}
                          {isTarget && (
                            <Chip size="small" color="success" label={t('board.ladder.target')} />
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
                <Button
                  size="small"
                  sx={{ mt: 2, textTransform: 'none' }}
                  onClick={() => setStageDialogOpen(true)}
                >
                  {t('board.changeStage')}
                </Button>
              </Paper>

              {MEMBERSHIP_MILESTONE_KEYS.some((k) => profile.milestones[k]) && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 1, fontWeight: 700 }}
                  >
                    {t('board.milestones.title')}
                  </Typography>
                  <Stack spacing={1} mt={1}>
                    {MEMBERSHIP_MILESTONE_KEYS.filter((k) => profile.milestones[k]).map((k) => (
                      <Stack key={k} direction="row" justifyContent="space-between" spacing={2}>
                        <Typography variant="body2">{t(`board.milestones.${k}`)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(profile.milestones[k]!)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Box>

          {/* ── Right: readiness + navigation ────────────────────────────── */}
          <Box flex={1} width="100%">
            <Stack spacing={2.5}>
              {readiness ? (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" mb={2}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                      {t('board.overallStatus.title')}
                    </Typography>
                    <Chip
                      icon={
                        readiness.overallStatus === 'ready' ? (
                          <CheckCircleRounded />
                        ) : (
                          <WarningAmberRounded />
                        )
                      }
                      color={readiness.overallStatus === 'ready' ? 'success' : 'warning'}
                      label={t(`board.overallStatus.${readiness.overallStatus}`)}
                    />
                  </Stack>

                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 1, fontWeight: 700 }}
                  >
                    {t('board.progressByCategory')}
                  </Typography>
                  <Stack spacing={1.5} mt={1}>
                    {readiness.byCategory.map((c) => {
                      const pct = c.total > 0 ? Math.round((c.met / c.total) * 100) : 0;
                      return (
                        <Box key={c.category}>
                          <Stack direction="row" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">{t(`categories.${c.category}`)}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {c.met}/{c.total}
                              {/* Lo que falta RESPONDER se dice con palabras, no
                                  descontándolo del denominador: el total es el
                                  tamaño del examen de TMAI, no del seminario. */}
                              {c.noData > 0 && (
                                <Box component="span" sx={{ ml: 1, opacity: 0.75 }}>
                                  · {t('board.categoryNoData', { count: c.noData })}
                                </Box>
                              )}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                      );
                    })}
                  </Stack>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 2 }}
                  >
                    {t('board.lastRun', {
                      date: formatDate(readiness.generatedAt),
                    })}
                  </Typography>
                </Paper>
              ) : (
                <Paper variant="outlined" sx={{ p: 4, borderRadius: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary" mb={2}>
                    {t('board.neverRun')}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={
                      runReadiness.isPending ? <CircularProgress size={16} /> : <RefreshRounded />
                    }
                    disabled={runReadiness.isPending}
                    onClick={() => void recalc()}
                  >
                    {t('board.runFirst')}
                  </Button>
                </Paper>
              )}

              {readiness && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 1, fontWeight: 700 }}
                  >
                    {t('packet.sectionTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5} mb={2}>
                    {t('packet.sectionBody')}
                  </Typography>
                  <Stack spacing={1.5}>
                    <Button
                      variant="contained"
                      startIcon={
                        assemblePacket.isPending ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DescriptionRounded />
                        )
                      }
                      disabled={assemblePacket.isPending}
                      sx={{ textTransform: 'none' }}
                      onClick={() => void generatePacket()}
                    >
                      {t('packet.generate')}
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<GroupsRounded />}
                        sx={{ textTransform: 'none' }}
                        onClick={() => setAnnexDialogOpen(true)}
                      >
                        {t('annex.generate')}
                      </Button>
                    )}
                  </Stack>
                </Paper>
              )}

              {/* PRESENTAR A LA RED. Va DESPUÉS del paquete a propósito: primero
                  se genera el documento para la junta, después se decide si se
                  entrega. El orden de la pantalla es el orden del trámite. */}
              <NetworkSubmissionSection
                tenantId={tenantId ?? ''}
                networkTenantId={networkTenantId}
                readiness={readiness}
                cycleYear={new Date().getFullYear()}
              />

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                <Stack spacing={1.5}>
                  <Button
                    variant="outlined"
                    endIcon={<ArrowForwardRounded />}
                    sx={{ textTransform: 'none', justifyContent: 'space-between' }}
                    onClick={() => navigate('/cap/institucional/criterios')}
                  >
                    {t('board.links.criteria')}
                  </Button>
                  <Button
                    variant="outlined"
                    endIcon={<ArrowForwardRounded />}
                    sx={{ textTransform: 'none', justifyContent: 'space-between' }}
                    onClick={() => navigate('/cap/institucional/atestaciones')}
                  >
                    {t('board.links.attestations')}
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* ── Change-stage dialog ─────────────────────────────────────────── */}
      <Dialog
        open={stageDialogOpen}
        onClose={() => setStageDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('board.changeStage')}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {stagePicker}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStageDialogOpen(false)} disabled={setStage.isPending}>
            {t('actions.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={setStage.isPending ? <CircularProgress size={16} /> : undefined}
            disabled={setStage.isPending}
            onClick={() => void saveStage()}
          >
            {t('board.entry.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Roster annex (PII) warning dialog ────────────────────────────── */}
      <Dialog
        open={annexDialogOpen}
        onClose={() => setAnnexDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('annex.dialog.title')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 2 }}>
            {t('annex.dialog.warning')}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t('annex.dialog.body')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAnnexDialogOpen(false)} disabled={buildRosterAnnex.isPending}>
            {t('actions.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={buildRosterAnnex.isPending ? <CircularProgress size={16} /> : undefined}
            disabled={buildRosterAnnex.isPending}
            onClick={() => void generateAnnex()}
          >
            {t('annex.dialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </Box>
  );
}
